/**
 * 一体化学习页（单词 / 句子共用外壳）
 * ------------------------------------------------------------
 * 学生视角只有一件事：「学完这个单元的词 / 句子」，看到一条连续进度条。
 * 内部由 WordStudyFlow / SentenceStudyFlow 决定下一步做什么，
 * 因此不会再出现「挑战 1 / 2 / 3」这种关卡感。
 *
 * 反馈走 components/feedback.js：答对星星+音效+英文表扬，答错温柔提示并允许重试。
 */
import { getUnitContent } from '../services/dataSource.js';
import { errorRefIds } from '../engines/sampler.js';
import { WordStudyFlow, MASTERY_GOAL } from '../engines/wordFlow.js';
import { SentenceStudyFlow, RECALL_MS } from '../engines/sentenceFlow.js';
import { saveSession } from '../services/records.js';
import { settleSession } from '../services/rewards.js';
import { renderAnswerZone, bindAnswerZone, renderFeedback } from '../components/quiz.js';
import { praiseBar, encourageBar, burstStars, playCorrectSound, playWrongSound, unlockAudio, scorePop } from '../components/feedback.js';
import { progressBar } from '../components/ui.js';
import { navigate } from '../core/router.js';
import { speakEn, speakZh, escapeHtml, fmtDuration, toast, sample, shuffle } from '../utils/index.js';
import {
  audioDiagnostics, playItem, playSentenceEn, playWord, primeAudio, stopAudio,
} from '../services/audioService.js';

const cfgKey = 'pending-flow';

function readCfg() {
  try {
    const raw = sessionStorage.getItem(cfgKey);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * 启动（由练习中心调用）
 * @param {object} cfg { module:'word'|'sentence', textbookId, unitId, limit, title }
 */
export function startFlow(opts = {}) {
  const { textbookId, unitId, module, limit, title, ids } = opts;
  sessionStorage.setItem(cfgKey, JSON.stringify({
    module: module === 'sentence' ? 'sentence' : 'word',
    textbookId, unitId, limit, title, ids: Array.isArray(ids) ? ids : undefined, at: Date.now(),
  }));
  navigate('#/flow');
}

export default async function renderFlow(mount, params = {}) {
  // 兜底：跳转与写入 sessionStorage 之间可能有先后差，允许重试一次
  let cfg = readCfg();
  if (!cfg?.unitId) {
    await new Promise(r => setTimeout(r, 120));
    cfg = readCfg();
  }
  // 仍然没有但 URL 带了 module + unitId，就直接用 URL 参数起步
  if (!cfg?.unitId && params.unitId) {
    cfg = { module: params.module || 'word', textbookId: params.textbookId, unitId: params.unitId };
  }
  if (!cfg?.unitId) {
    mount.innerHTML = `<div class="empty"><span class="icon">🤔</span>
      <p><strong>没有正在进行的任务</strong></p>
      <p class="muted">请先在练习中心选择「单词学习」或「句子学习」。</p>
      <a class="btn btn-primary" href="#/practice">去练习中心</a></div>`;
    return;
  }

  const content = await getUnitContent(cfg.textbookId, cfg.unitId);
  const priority = await errorRefIds(cfg.unitId).catch(() => new Set());
  const module = cfg.module === 'sentence' ? 'sentence' : 'word';

  if (module === 'word') {
    if (!content.words.length) return renderEmpty(mount, '本单元还没有单词', '先导入教材内容');
    const flow = new WordStudyFlow({
      words: content.words, unitId: cfg.unitId, textbookId: cfg.textbookId,
      priority, limit: cfg.limit, ids: cfg.ids, title: cfg.title || '单词学习',
    });
    runFlow(mount, flow, cfg, 'word');
  } else {
    if (!content.sentences.length) return renderEmpty(mount, '本单元还没有句子', '先导入教材内容');
    const flow = new SentenceStudyFlow({
      sentences: content.sentences, unitId: cfg.unitId, textbookId: cfg.textbookId,
      priority, limit: cfg.limit, ids: cfg.ids, title: cfg.title || '句子学习',
    });
    runFlow(mount, flow, cfg, 'sentence');
  }
}

function renderEmpty(mount, title, sub) {
  mount.innerHTML = `<div class="empty"><span class="icon">📭</span>
    <p><strong>${escapeHtml(title)}</strong></p><p class="muted">${escapeHtml(sub)}</p>
    <a class="btn btn-primary" href="#/import">去导入教材</a></div>`;
}

/* ------------------------------ 主循环 ------------------------------ */

function runFlow(mount, flow, cfg, module) {
  // 调试/自动化测试钩子：只读暴露当前流程对象，不影响业务逻辑
  try { window.__FLOW__ = flow; } catch { /* ignore */ }
  unlockAudio();             // 首次点击已解锁音频，这里再兜一次
  let answered = false;
  let recallTimer = null;
  let showingSentence = false;

  /**
   * 共享的提交处理：判分 → 反馈 → 下一步。
   * 闪卡环节和普通答题环节共用同一套逻辑，避免两处各写一遍导致作用域 bug。
   */
  function handleSubmit(value, question, opts = {}) {
    const isApply = flow.current?.step === 'apply';
    const result = isApply && !opts.keep ? flow.submitApply(value) : flow.submit(value, opts);
    return result;
  }

  const stepName = step => {
    const list = module === 'word'
      ? { meet: '认识单词', 'listen-pick': '听音选词', 'en-zh': '看英文选中文', 'zh-en': '看中文选英文', 'listen-spell': '听音拼写', write: '独立默写', context: '例句填空', apply: '用一用' }
      : { understand: '读懂句子', recall: '记忆闪卡', fill: '补全句子', order: '单词重排', translate: '中文译英文', transform: '句型替换', apply: '用一用' };
    return list[step] || '继续';
  };

  /** 顶部：一条总进度 + 掌握中的词/句 */
  function topBar() {
    if (module === 'word') {
      const m = flow.masteryCount();
      return `<div class="flow-top">
        <button class="icon-btn" id="f-quit" type="button" title="退出">✕</button>
        <div class="flow-progress">
          <div class="flow-progress-head">
            <span>${escapeHtml(flow.title)}</span>
            <span class="flow-count">已掌握 <b>${m.done}</b> / ${m.total} 个词</span>
          </div>
          ${progressBar(flow.progress * 100)}
        </div>
      </div>`;
    }
    const done = flow.records.length;
    return `<div class="flow-top">
      <button class="icon-btn" id="f-quit" type="button" title="退出">✕</button>
      <div class="flow-progress">
        <div class="flow-progress-head">
          <span>${escapeHtml(flow.title)}</span>
          <span class="flow-count">${flow.sentences.length} 句 · 已完成 ${done} 步</span>
        </div>
        ${progressBar(flow.progress * 100)}
      </div>
    </div>`;
  }

  /** 单词掌握气泡（★☆☆ 形式，一眼看懂还差几次） */
  function masterDots(wordId) {
    const st = module === 'word' ? flow.stats.get(wordId) : null;
    if (!st) return '';
    return `<span class="master-dots" title="独立答对 ${st.correct}/${MASTERY_GOAL} 次即掌握">
      ${Array.from({ length: MASTERY_GOAL }, (_, i) => `<i class="${i < st.correct ? 'on' : ''}">★</i>`).join('')}
    </span>`;
  }

  function bindQuit() {
    mount.querySelector('#f-quit')?.addEventListener('click', async () => {
      if (!flow.records.length) { navigate('#/practice'); return; }
      await finish(mount, flow, cfg, module, { aborted: true });
    });
  }

  /* ---------------- 单词：认识卡片 ---------------- */
  function renderMeet() {
    const task = flow.current;
    const w = flow.stats.get(task.wordId).word;
    mount.innerHTML = `
      ${topBar()}
      <div class="flow-card meet-card">
        <div class="meet-tag">认识一下</div>
        <div class="meet-word">${escapeHtml(w.en)}</div>
        ${w.phonetic ? `<div class="meet-phon">${escapeHtml(w.phonetic)}</div>` : ''}
        <button class="speak-big" id="m-speak" type="button">🔊 再听一次</button>
        <div class="audio-note" id="m-audio-note" hidden></div>
        <div class="meet-zh">${escapeHtml(w.zh)}</div>
        ${w.sentence ? `<div class="meet-sentence">
            <div class="ms-en">${highlight(w.sentence, w.en)}</div>
            ${w.sentenceZh ? `<div class="ms-zh">${escapeHtml(w.sentenceZh)}</div>` : ''}
            <button class="btn btn-sm btn-ghost" id="m-say" type="button">🔊 听例句</button>
          </div>` : ''}
        ${w.phrase ? `<div class="meet-phrase">${escapeHtml(w.phrase)} · ${escapeHtml(w.phraseZh || '')}</div>` : ''}
        <button class="btn btn-primary btn-lg btn-block mt-16" id="m-next" type="button">记住了，下一个 →</button>
      </div>`;

    // 单词对象自带 audio 字段（en/zh/audio 在同一个对象里，绝不靠下标对应）
    const say = async () => {
      const r = await playWord(w);
      showAudioNote(r);
      return r;
    };
    mount.querySelector('#m-speak').addEventListener('click', say);
    mount.querySelector('#m-say')?.addEventListener('click', async () => {
      const r = await playSentenceEn(w.sentence);
      showAudioNote(r);
    });

    // 第一次出现：先自动播英文单词，播完再播例句。
    // 「开始学习」按钮里已经做过音频解锁（primeAudio），这里紧跟播放即可。
    setTimeout(async () => {
      if (flow.current?.wordId !== task.wordId) return;   // 已翻页则不再播
      const r = await say();
      if (!r.ok) return;                                  // 单词都没播出来就不接着播例句，避免噪音叠加
      if (w.sentence && flow.current?.wordId === task.wordId) {
        playSentenceEn(w.sentence);
      }
    }, 300);

    mount.querySelector('#m-next').addEventListener('click', () => {
      stopAudio();          // 翻页前掐掉正在播的音频，避免下一个单词和上一个重叠
      flow.ackMeet();
      step();
    });
    bindQuit();
  }

  /** 把播放失败的原因明确显示出来，而不是"点了没反应" */
  function showAudioNote(res) {
    const box = mount.querySelector('#m-audio-note') || mount.querySelector('#q-audio-note');
    if (!box) return;
    if (!res || res.ok) {
      box.hidden = true;
      box.textContent = '';
      return;
    }
    box.hidden = false;
    box.innerHTML = `⚠ ${escapeHtml(res.error || '音频播放失败')}
      <button class="btn btn-sm btn-ghost" type="button" id="audio-diag">诊断</button>`;
    box.querySelector('#audio-diag')?.addEventListener('click', () => {
      const d = audioDiagnostics();
      toast(`音频诊断：MP3=${d.audioSupported ? '可用' : '不可用'}；语音合成=${d.ttsSupported ? '支持' : '不支持'}；`
        + `英文语音=${d.englishVoice || '未检测到'}；解锁=${d.primed ? '是' : '否'}`, 'warn', 5200);
    });
  }

  /* ---------------- 句子：理解卡片 ---------------- */
  function renderUnderstand() {
    const s = flow.current && flow._sentenceOf(flow.current);
    if (!s) return step();
    mount.innerHTML = `
      ${topBar()}
      <div class="flow-card meet-card">
        <div class="meet-tag">先读懂它</div>
        <div class="meet-sentence-en">${escapeHtml(s.en)}</div>
        <div class="meet-zh">${escapeHtml(s.zh)}</div>
        ${s.pattern ? `<div class="pattern-chip">句型：${escapeHtml(s.pattern)}</div>` : ''}
        <p class="muted small mt-16">接下来会先给你 3 秒看这句话，然后它会被藏起来，看你能记住多少。</p>
        <button class="btn btn-primary btn-lg btn-block" id="u-next" type="button">我记住了 →</button>
      </div>`;
    // 句子模块暂不朗读（TTS 稳定后再加回），只做视觉记忆
    mount.querySelector('#u-next').addEventListener('click', () => {
      flow.ackUnderstand();
      step();
    });
    bindQuit();
  }

  /* ---------------- 句子：3 秒闪卡后隐藏 ---------------- */
  function renderRecall() {
    const task = flow.current;
    const s = flow._sentenceOf(task);
    if (!s) return step();
    showingSentence = true;
    // 每次进入闪卡都完整重建，避免上一题的答题区残留在 DOM 里造成误判
    mount.innerHTML = `
      ${topBar()}
      <div class="flow-card recall-card">
        <div class="recall-hint">仔细看，<b id="r-timer">3</b> 秒后句子会消失</div>
        <div class="recall-sentence" id="r-sentence">${escapeHtml(s.en)}</div>
        <div class="recall-countdown"><span id="r-bar"></span></div>
        <div id="r-answer" class="recall-answer" hidden></div>
        <div id="r-feedback"></div>
      </div>`;
    const bar = mount.querySelector('#r-bar');
    const timerEl = mount.querySelector('#r-timer');
    bar.style.animationDuration = `${RECALL_MS}ms`;

    let left = RECALL_MS;
    recallTimer = setInterval(() => {
      left -= 100;
      const sec = Math.max(0, Math.ceil(left / 1000));
      if (timerEl) timerEl.textContent = String(sec);
      if (left <= 0) {
        clearInterval(recallTimer);
        recallTimer = null;
        hideSentence();
      }
    }, 100);

    function hideSentence() {
      if (!showingSentence) return;
      showingSentence = false;
      const box = mount.querySelector('#r-sentence');
      if (box) {
        box.classList.add('hidden-away');
        box.innerHTML = '🙈 句子藏起来了';
      }
      mount.querySelector('.recall-countdown')?.remove();
      mount.querySelector('.recall-hint')?.remove();
      const fbOld = mount.querySelector('#r-feedback');
      if (fbOld) fbOld.innerHTML = '';
      showAnswerZone();
    }

    function showAnswerZone() {
      const q = flow.currentQuestion();
      const zone = mount.querySelector('#r-answer');
      zone.hidden = false;
      zone.innerHTML = `
        <div class="recall-prompt">${escapeHtml(String(q.prompt).replace(/\n/g, ' ')).replace(/____/g, '<span class="blank-bar">____</span>')}</div>
        ${renderAnswerZone({ ...q, type: 'transform', placeholder: '凭记忆写出整句' })}
        <div class="row mt-8" style="gap:8px">
          <button class="btn btn-ghost btn-sm" id="r-show" type="button">👀 想不起来，看原句</button>
        </div>`;
      const host = zone;
      bindAnswerZone(host, { ...q, type: 'transform' }, value => {
        const result = handleSubmit(value, q);
        if (!result) return;
        const fb = mount.querySelector('#r-feedback') || (() => {
          const d = document.createElement('div');
          d.id = 'r-feedback';
          zone.appendChild(d);
          return d;
        })();
        host.querySelectorAll('button, input').forEach(b => { b.disabled = true; });
        if (result.correct) {
          playCorrectSound(true);
          burstStars(zone, 5);
          fb.innerHTML = praiseBar({ streak: flow.streak })
            + `<div class="explain-line">${escapeHtml(q.explain || '').replace(/\n/g, '<br>')}</div>
               <button class="btn btn-primary btn-lg btn-block mt-8" id="r-next" type="button">继续 →</button>`;
        } else {
          playWrongSound(true);
          fb.innerHTML = encourageBar({ canRetry: true, hint: q.hint || '' })
            + renderFeedback(result, q)
            + `<div class="row mt-8" style="gap:10px">
                 <button class="btn btn-primary btn-lg" id="r-retry" type="button">再试一次</button>
                 <button class="btn btn-ghost" id="r-next" type="button">看讲解，下一题 →</button>
               </div>`;
        }
        fb.querySelector('#r-next')?.addEventListener('click', () => step());
        fb.querySelector('#r-retry')?.addEventListener('click', () => {
          fb.innerHTML = '';
          const fresh = flow.currentQuestion();
          zone.innerHTML = `
            <div class="recall-prompt">${escapeHtml(String(fresh.prompt).replace(/\n/g, ' ')).replace(/____/g, '<span class="blank-bar">____</span>')}</div>
            ${renderAnswerZone({ ...fresh, type: 'transform', placeholder: '凭记忆写出整句' })}`;
          bindAnswerZone(zone, { ...fresh, type: 'transform' }, v2 => handleSubmit(v2, fresh, { keep: true }) && step());
        });
      });
      mount.querySelector('#r-show')?.addEventListener('click', () => {
        flow.useHint();
        toast(`原句：${s.en}`, 'info', 4000);
      });
    }

    bindQuit();
  }

  /* ---------------- 通用答题 ---------------- */
  function renderQuiz() {
    const task = flow.current;
    if (!task) return step();
    const q = flow.currentQuestion();
    if (!q) {
      // 这一步出不了题（内容缺失/应用任务没准备好）→ 丢弃该任务继续前进，
      // 绝不能 return step()：那会在同一状态反复进出 renderQuiz 直接把栈打爆。
      flow.queue.shift();
      flow._ensureWork?.();
      return step();
    }

    const wordId = module === 'word' ? task.wordId : null;
    const isApply = task.step === 'apply';

    mount.innerHTML = `
      ${topBar()}
      <div class="flow-card quiz-card">
        <div class="quiz-step-row">
          <span class="step-chip">${escapeHtml(stepName(isApply ? 'apply' : task.step))}</span>
          ${wordId ? masterDots(wordId) : ''}
        </div>
        <div class="prompt-text" id="q-prompt">${escapeHtml(String(q.prompt || '')).replace(/\n/g, '<br>')}</div>
        ${q.audio && module === 'word' ? `<div class="row" style="gap:8px;margin:10px 0">
            <button class="btn btn-sm btn-round" id="q-speak" type="button">🔊 再听一次</button>
          </div>
          <div class="audio-note" id="q-audio-note" hidden></div>` : ''}
        <div id="q-body"></div>
        <div id="q-feedback"></div>
        <div class="row mt-8" style="justify-content:center">
          <button class="btn btn-ghost btn-sm" id="q-hint" type="button" ${q.hint ? '' : 'disabled'}>💡 给我一点提示</button>
        </div>
      </div>`;

    if (q.audio && module === 'word') {
      // 优先播这个单词自己的 MP3；没有 MP3 才回退系统语音
      const playQ = async () => {
        const r = await playItem({ audio: q.audioUrl, text: q.audio, lang: 'en-US' });
        showAudioNote(r);
        return r;
      };
      mount.querySelector('#q-speak').addEventListener('click', playQ);
      // 听音环节（听音选词 / 听音拼写）自动播放一次；之后可点按钮重听
      if (q.type === 'listen' || task.step === 'listen-spell') {
        setTimeout(() => {
          if (mount.querySelector('#q-speak')) playQ();
        }, 300);
      }
    }

    const body = mount.querySelector('#q-body');
    // 语境应用一律单行输入，避免空数不对
    const renderQ = module === 'word' && task.step === 'context' && q.type === 'fill'
      ? { ...q, type: 'transform', placeholder: '填入缺少的那个词' }
      : q;
    body.dataset.qtype = renderQ.type === 'transform' ? 'text' : 'choice';
    body.innerHTML = renderAnswerZone(renderQ);
    bindAnswerZone(body, renderQ, value => submit(value, q));

    mount.querySelector('#q-hint')?.addEventListener('click', () => {
      flow.useHint();
      toast(`💡 ${q.hint}`, 'info', 3200);
      mount.querySelector('#q-hint').disabled = true;
    });

    bindQuit();
    answered = false;

    function submit(value, question, opts = {}) {
      if (answered) return;
      answered = true;
      const result = handleSubmit(value, question, opts);
      if (!result) return;

      body.querySelectorAll('button, input').forEach(b => { b.disabled = true; });
      body.querySelectorAll('.opt').forEach(b => {
        if (b.dataset.value === (result.expected || '')) b.classList.add('correct');
        else if (!result.correct && b.dataset.value === value) b.classList.add('wrong');
      });

      const fb = mount.querySelector('#q-feedback');
      if (result.correct) {
        playCorrectSound(true);
        burstStars(body, 5);
        scorePop(body, '+1');
        if (result.masteredNow) {
          burstStars(mount.querySelector('.flow-card'), 10);
          toast(`🌟 ${result.wordEn} 掌握啦！`, 'success', 2000);
        }
        fb.innerHTML = praiseBar({ streak: flow.streak })
          + `<div class="explain-line">${escapeHtml(question.explain || '').replace(/\n/g, '<br>')}</div>`
          + `<button class="btn btn-primary btn-lg btn-block mt-16" id="q-next" type="button">继续 →</button>`;
      } else {
        playWrongSound(true);
        fb.innerHTML = encourageBar({ canRetry: true, hint: question.hint || '' })
          + renderFeedback(result, { ...question, explain: question.explain })
          + `<div class="row mt-8" style="gap:10px">
              <button class="btn btn-primary btn-lg" id="q-retry" type="button">再试一次</button>
              <button class="btn btn-ghost" id="q-next" type="button">看讲解，下一题 →</button>
             </div>`;
        // 答错也能听一遍正确读音（仅单词模块；句子模块暂不朗读）
        if (module === 'word') {
          const target = flow.stats.get(task.wordId)?.word;
          if (target) playWord(target);
          else speakEn(result.expected || question.answer?.[0] || '');
        }
      }

      bindForward(fb, { q: question, onSubmit: submit });
    }
  }

  function bindForward(host, ctx = {}) {
    const next = host.querySelector('#q-next');
    const retry = host.querySelector('#q-retry');
    next?.addEventListener('click', () => step());
    retry?.addEventListener('click', () => {
      // 「再试一次」= 原地重答同一题：清空作答区，队列不动
      answered = false;
      const body = mount.querySelector('#q-body') || mount.querySelector('#r-answer');
      const fb = mount.querySelector('#q-feedback');
      if (fb) fb.innerHTML = '';
      if (body) {
        const q = ctx.q;
        const renderQ = body.dataset.qtype === 'text'
          ? { ...q, type: 'transform' }
          : q;
        body.innerHTML = renderAnswerZone(renderQ);
        bindAnswerZone(body, renderQ, v => ctx.onSubmit?.(v, q, { keep: true }));
        body.querySelectorAll('button, input').forEach(b => { b.disabled = false; });
      }
    });
    const onKey = e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        document.removeEventListener('keydown', onKey);
        (next || retry)?.click();
      }
    };
    document.addEventListener('keydown', onKey);
  }

  /* ---------------- 调度 ---------------- */
  let stepDepth = 0;
  function step() {
    // 进入新一步之前：收掉所有残留（倒计时、反馈、答题区），
    // 避免上一题的 DOM 被下一步的检测逻辑误判（曾导致"闪卡直接跳到答题"的诡异现象）。
    if (recallTimer) { clearInterval(recallTimer); recallTimer = null; }
    showingSentence = false;
    if (flow.finished) return finish(mount, flow, cfg, module, {});
    // 深度保护：正常情况下 step() 是用户点击驱动的，不会连续自调用；
    // 一旦出现连续自调用说明状态有问题，直接结算，不要卡死页面。
    stepDepth += 1;
    if (stepDepth > 6) {
      console.error('[flow] step 连续自调用过深，强制结算');
      stepDepth = 0;
      return finish(mount, flow, cfg, module, { aborted: true });
    }
    setTimeout(() => { stepDepth = 0; }, 0);
    const task = flow.current;
    if (module === 'word') {
      if (task.step === 'meet') return renderMeet();
      return renderQuiz();
    }
    if (task.step === 'understand') return renderUnderstand();
    if (task.step === 'recall') return renderRecall();
    return renderQuiz();
  }

  step();
}

/* ------------------------------ 结束 ------------------------------ */

async function finish(mount, flow, cfg, module, { aborted = false } = {}) {
  mount.innerHTML = '<div class="page-loading">正在结算…</div>';
  const summary = flow.summary();
  let reward = { xp: 0, level: null, unlocked: [] };
  try {
    await saveSession(summary);          // 先落库，成就判定依赖这次记录
    reward = await settleSession(summary);
    sessionStorage.removeItem(cfgKey);
  } catch (err) {
    console.error('[flow] 结算失败', err);
    toast('成绩保存失败：' + err.message, 'error');
  }

  const lv = reward.level || { level: 1, xp: 0, progress: 0, xpInLevel: 0 };
  const wrongs = summary.records.filter(r => !r.correct);
  const stars = summary.score >= 90 ? 3 : summary.score >= 70 ? 2 : summary.score >= 50 ? 1 : 0;

  const wordRows = module === 'word'
    ? `<div class="mastery-list">${flow.wordProgress().map(p => `
        <div class="mastery-row ${p.mastered ? 'ok' : ''}">
          <span class="mr-en">${escapeHtml(p.word.en)}</span>
          <span class="mr-zh">${escapeHtml(p.word.zh)}</span>
          <span class="master-dots">${Array.from({ length: MASTERY_GOAL }, (_, i) => `<i class="${i < p.correct ? 'on' : ''}">★</i>`).join('')}</span>
          ${p.mastered ? '<span class="tag green">已掌握</span>' : `<span class="tag orange">还差 ${MASTERY_GOAL - p.correct} 次</span>`}
        </div>`).join('')}</div>`
    : `<div class="mastery-list">${flow.sentenceProgress().map(p => `
        <div class="mastery-row ${p.wrongSteps.length ? '' : 'ok'}">
          <span class="mr-en">${escapeHtml(p.sentence.en)}</span>
          ${p.wrongSteps.length
            ? `<span class="tag orange">吃力环节：${[...new Set(p.wrongSteps)].join('、')}</span>`
            : '<span class="tag green">顺利完成</span>'}
        </div>`).join('')}</div>`;

  mount.innerHTML = `
    <div class="result-wrap flow-result">
      <div class="stars-hero">${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}</div>
      <h2 class="result-title">${stars === 3 ? 'Great job! 太棒了！' : stars === 2 ? 'Well done! 做得不错！' : stars === 1 ? 'Good try! 继续加油！' : 'Nice start! 我们再来一次'}</h2>
      <div class="result-sub">${aborted ? '（提前结束）' : ''}共 ${summary.total} 步 · 答对 ${summary.correct} 步 · 用时 ${fmtDuration(summary.durationMs)}</div>

      <div class="grid c2 mt-16">
        <div class="mini-card">
          <div class="xp-gain">+${reward.xp} <small>XP</small></div>
          <div class="muted small">当前 Lv.${lv.level}</div>
          ${progressBar((lv.progress ?? 0) * 100)}
        </div>
        <div class="mini-card">
          <div class="mini-stat"><b>${summary.independent}</b><span>✓ 独立完成</span></div>
          <div class="mini-stat"><b>${summary.hinted}</b><span>△ 提示后</span></div>
          <div class="mini-stat"><b>${summary.failed}</b><span>× 需再练</span></div>
        </div>
      </div>

      ${(reward.unlocked || []).length ? `<div class="mini-card mt-16">
        <div class="mini-title">🎉 新解锁成就</div>
        ${reward.unlocked.map(a => `<div class="achv-line"><span>${a.icon || '🏅'}</span><b>${escapeHtml(a.name)}</b><small>+${a.xp || 0} XP</small></div>`).join('')}
      </div>` : ''}

      <div class="mini-card mt-16">
        <div class="mini-title">${module === 'word' ? '每个词的掌握情况' : '每句话的情况'}</div>
        ${wordRows}
      </div>

      ${wrongs.length ? `<div class="mini-card mt-16">
        <div class="mini-title">还需要再练（${wrongs.length}）</div>
        ${wrongs.slice(0, 8).map(w => `<div class="wrong-line">
          <span class="grow">${escapeHtml(String(w.prompt).replace(/\n/g, ' ').slice(0, 60))}</span>
          <span class="sym-ok">${escapeHtml(w.expected || '')}</span>
        </div>`).join('')}
        <p class="muted small mt-8">这些已经进入错题本，可以在「复习」里专门练。</p>
      </div>` : ''}

      <div class="row mt-16" style="gap:10px;justify-content:center;flex-wrap:wrap">
        <a class="btn btn-primary btn-lg" href="#/practice">继续学别的</a>
        <button class="btn btn-ghost" id="again" type="button">再来一轮</button>
        <a class="btn btn-ghost" href="#/review">去复习错题</a>
        <a class="btn btn-ghost" href="#/report">看学习数据</a>
      </div>
    </div>`;

  mount.querySelector('#again')?.addEventListener('click', () => {
    sessionStorage.setItem(cfgKey, JSON.stringify({ ...cfg, at: Date.now() }));
    navigate('#/flow');
  });
}

/** 把例句里的目标词高亮 */
function highlight(sentence, word) {
  const s = String(sentence || '');
  const w = String(word || '');
  if (!w) return escapeHtml(s);
  const re = new RegExp(`(${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
  return escapeHtml(s).replace(re, '<mark>$1</mark>');
}
